// number.util.ts

export type ValidateResult = { status: boolean; message?: string };

export const addComma = (value?: number) => {
  return value != null ? value.toLocaleString() : "";
};

export const formatSign = (value?: number, withPercent?: true) => {
  const suffix = withPercent ? "%" : "";

  if (value == null) {
    return "";
  }

  if (value === 0) {
    return `±${value}${suffix}`;
  }

  if (value > 0) {
    return `+${value}${suffix}`;
  }

  return `${value}${suffix}`;
};

export const toNumber = (value: any) => {
  if (value == null || value === "") {
    return null;
  }
  return Number(value);
};

/**
 *
 * @param value
 * @param type round = 四捨五入、ceil = 切り上げ、 floor = 切り捨て
 * @param digit 少数第何位でやるか(1 -> 整数, 2 -> 少数第二位切り上げ)
 * @returns 数値としておかしい場合はnullが返却
 */
export const round = (
  value: any,
  type: "round" | "ceil" | "floor",
  digit: number
) => {
  const v = toNumber(value);

  if (v == null || isNaN(v)) {
    return null;
  }

  const d = Math.pow(10, digit);
  const tmp = v * d;

  if (type === "round") {
    return Math.round(tmp) / d;
  }

  if (type === "ceil") {
    return Math.ceil(tmp) / d;
  }
  return Math.floor(tmp) / d;
};

/**
 * 文字列から数字以外を除去（半角数字のみ）する
 * @param value 入力値
 * @param maxLen 桁数制限（任意）
 */
export const digitsOnly = (value: string, maxLen?: number) => {
  const d = (value ?? "").replace(/\D/g, "");
  return maxLen != null ? d.slice(0, maxLen) : d;
};



/**
 * 整数（半角数字のみ）＋未入力許容＋範囲チェック
 */
export type IntegerValidatorOptions = {
  allowEmpty?: boolean; // 未入力OK
  min?: number; // 最小値
  max?: number; // 最大値
  label?: string; // メッセージ用名称
};

export const validateIntegerWith = (
  value: string,
  opts: IntegerValidatorOptions = {}
): ValidateResult => {
  const label = opts.label ?? "入力値";
  const raw = (value ?? "").trim();

  if (raw.length === 0) {
    return opts.allowEmpty ? { status: true } : { status: false, message: `${label}を入力してください` };
  }

  if (!/^\d+$/.test(raw)) {
    return { status: false, message: `${label}は半角数字のみ入力してください` };
  }

  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return { status: false, message: `${label}が不正です` };
  }

  if (opts.min != null && n < opts.min) {
    return { status: false, message: `${label}は${opts.min}以上で入力してください` };
  }
  if (opts.max != null && n > opts.max) {
    return { status: false, message: `${label}は${opts.max}以下で入力してください` };
  }

  return { status: true };
};

export type PostalCodeValidatorOptions = {
  allowEmpty?: boolean; // 未入力OK
  label?: string; // メッセージ用名称
};

/**
 * 拡張版：郵便番号（123-4567）＋未入力許容
 */
export const validatePostalCodeWith = (
  value: string,
  opts: PostalCodeValidatorOptions = {}
): ValidateResult => {
  const label = opts.label ?? "郵便番号";
  const raw = (value ?? "").trim();

  if (raw.length === 0) {
    return opts.allowEmpty ? { status: true } : { status: false, message: `${label}を入力してください` };
  }

  const regex = /^\d{3}-\d{4}$/;
  if (!regex.test(raw)) {
    return { status: false, message: `${label}は123-4567の形式で入力してください` };
  }
  return { status: true };
};
